import { useLocation } from 'react-router-dom';
import { t } from '@app/i18n';
import { Alert, Card, ErrorBanner, PageHeader } from '@zarya/ui';
import { useIdentities } from '@app/main/Decisions';
import { ResultBlock } from './ResultBlock';
import { SimulatorForm } from './SimulatorForm';
import { useSimulate } from './Simulator.api';
import {
  simulationBody,
  defaultsWithPreenchimento,
  type DecisionPreenchimento,
} from './SimulatorModel';
import s from './Simulator.module.css';

/**
 * T18 — Simulador de autorização. "Esta chamada passaria hoje?", sem executar
 * nada (FR-100).
 *
 * Aberto direto pelo menu ou por **Reproduzir no simulador** em T17, que manda
 * identidade, tool e as claims que a decisão guardou por estado de rota.
 *
 * Disponível a **todos os papéis**, viewer incluído (§5.2, cenário 10.9): simular
 * é leitura — não muda contrato, não publica política, não executa a chamada.
 */

export function SimulatorPage() {
  const location = useLocation();
  const preenchimento = (location.state ?? null) as DecisionPreenchimento | null;
  const defaults = defaultsWithPreenchimento(preenchimento);

  // `com_contrato=true`: o PDP avalia contra o documento publicado, e identidade
  // sem contrato ativo devolve 409 `sem_contrato_ativo`.
  const identidades = useIdentities(true);
  const simulation = useSimulate();

  const eligible = identidades.data ?? [];
  const withoutEligible = identidades.isSuccess && eligible.length === 0;

  return (
    <>
      <PageHeader
        // O título é o mesmo rótulo do menu (`shell.nav.simulator`), como nas
        // outras catorze: quem clica em "Simulador" tem de chegar numa tela que
        // se chama "Simulador". A chave é desta tela, e não do `shell`, pela
        // convenção do bloco 1: cada tela é dona do próprio título.
        title={t('simulator.page.title')}
        icon="play"
        tip={t('simulator.page.purpose')}
        updatedLabel={t('simulator.page.updated')}
      />

      {identidades.isError ? (
        <ErrorBanner
          error={identidades.error}
          title={t('simulator.page.identitiesError')}
          onRetry={() => void identidades.refetch()}
        />
      ) : null}

      {/* FR-101: sem identidade elegível não há o que simular — e o motivo é dito. */}
      {withoutEligible ? (
        <Alert tone="info" title={t('simulator.page.noEligible.title')}>
          {t('simulator.page.noEligible.body')}
        </Alert>
      ) : null}

      <div className={s.grade}>
        <Card title={t('simulator.call.title')} subtitle={t('simulator.call.subtitle')} fill>
          {identidades.isLoading ? (
            <p className={s.vazio}>{t('simulator.page.loadingIdentities')}</p>
          ) : null}

          {eligible.length > 0 ? (
            <SimulatorForm
              // `key`: quando T17 manda um pré-preenchimento novo, o formulário
              // remonta com ele em vez de manter o que estava digitado.
              key={`${preenchimento?.identityId ?? ''}|${preenchimento?.tool ?? ''}|${preenchimento?.agent ?? ''}|${defaults.claims}`}
              defaultValues={defaults}
              claimsFromDecision={defaults.subjectMode === 'claims'}
              identidades={eligible.map((identidade) => ({
                value: identidade.id,
                label: identidade.slug ?? t('simulator.page.identityOption.unassigned'),
                hint: identidade.spiffe_id_predicted ?? undefined,
              }))}
              pending={simulation.isPending}
              onSubmit={(valores) => simulation.mutate(simulationBody(valores))}
            />
          ) : null}
        </Card>

        <Card title={t('simulator.result.title')} fill>
          {simulation.isError ? (
            <ErrorBanner
              error={simulation.error}
              title={t('simulator.result.error')}
              onRetry={() => simulation.reset()}
              retryLabel={t('simulator.result.error.retry')}
            />
          ) : null}

          {simulation.isPending ? <p className={s.vazio}>{t('simulator.result.pending')}</p> : null}

          {!simulation.data && !simulation.isPending && !simulation.isError ? (
            <p className={s.vazio}>{t('simulator.result.empty')}</p>
          ) : null}

          {simulation.data && !simulation.isPending ? (
            <ResultBlock simulacao={simulation.data} />
          ) : null}
        </Card>
      </div>
    </>
  );
}
